import { FolderCheck, UploadCloud } from 'lucide-react'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'

import { Card } from '@/components/card/card'

import { cn } from '@/lib/utils'

export interface FileInputProps {
  file: File | null
  onSelect: (file: File | null) => void
}

export const ImportFileInput: React.FC<FileInputProps> = ({ file, onSelect }) => {
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return
      }

      onSelect(acceptedFiles[0])
    },
    [onSelect]
  )

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    onDrop: handleDrop,
    maxFiles: 1,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  })

  if (!file) {
    return (
      <div
        className={cn(
          'flex h-[271px] flex-col items-center justify-center rounded-lg bg-muted',
          isDragAccept && 'ring-2 ring-black/75'
        )}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <UploadCloud className="text-muted-foreground" size={58} />
        <div className="my-2 font-medium text-muted-foreground">Select your file or drag and drop it here</div>
        <div className="text-sm font-medium text-muted-foreground">.csv, .xlsx or .txt</div>
      </div>
    )
  }

  return (
    <div>
      <Card className="mb-10 flex h-[142px] items-center justify-between space-x-[30px] rounded-lg border-green-400 bg-green-50">
        <FolderCheck className="ml-10 text-green-400" size={60} />
        <div className="flex flex-1 flex-col space-y-2">
          <div className="flex justify-between">
            <div className="font-medium">Your file is ready to import. Please confirm to finalize the upload.</div>
            <button
              type="button"
              className="mr-6 border-b border-black leading-4 underline"
              onClick={() => {
                onSelect(null)
              }}
            >
              Delete
            </button>
          </div>
          <div className="text-sm font-medium text-muted-foreground">{file.name}</div>
        </div>
      </Card>
    </div>
  )
}
